import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AdminNavigationDrawer } from '../../components/admin/AdminNavigationDrawer';
import { useOTPAuth } from '../../hooks/useOTPAuth';
import { adminService, ChatMessage } from '../../services/adminService';

export const SessionDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user, logout, createAnonymousSession } = useOTPAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedToolSections, setExpandedToolSections] = useState<Set<string>>(new Set());
  const [sessionIdCopied, setSessionIdCopied] = useState(false);
  const [costBreakdownExpanded, setCostBreakdownExpanded] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = useState(false);

  const handleLogout = async () => {
    await logout();
    await createAnonymousSession();
    window.location.href = '/chat';
  };

  // Fetch session history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await adminService.getSessionHistory(sessionId);
        setMessages(data);
      } catch (err: any) {
        console.error('Error fetching session history:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load session history');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [sessionId]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const toggleStepsExpanded = (messageId: string) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const toggleToolSection = (sectionKey: string) => {
    setExpandedToolSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  const handleCopySessionId = async () => {
    if (!sessionId) return;
    try {
      await navigator.clipboard.writeText(sessionId);
      setSessionIdCopied(true);
      setTimeout(() => setSessionIdCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy session ID:', err);
    }
  };

  const calculateTotalCost = () => {
    return messages.reduce((sum, msg) => {
      return sum + msg.steps.reduce((stepSum, step) => {
        const stepCost = step.estimatedCostUsd || 0;
        return stepSum + stepCost;
      }, 0);
    }, 0);
  };

  const calculateCostByTool = () => {
    const costByTool: { [toolName: string]: number } = {};

    messages.forEach((msg) => {
      msg.steps.forEach((step) => {
        // Use actual model name for LLM activities, otherwise use tool name
        const toolName = step.llmActivity
          ? `${step.llmActivity.modelName} (${step.llmActivity.provider})`
          : step.toolUse || 'Unknown';
        const stepCost = step.estimatedCostUsd || 0;
        costByTool[toolName] = (costByTool[toolName] || 0) + stepCost;
      });
    });

    return costByTool;
  };

  const userInfo = messages.length > 0 ? messages[0] : null;
  const firstMessageTime = messages.length > 0 ? messages[0].createdAt : null;
  const lastActivity = messages.length > 0 ? messages[messages.length - 1].createdAt : null;

  return (
    <div className="flex h-screen bg-neutral-white dark:bg-neutral-black">
      {/* Admin Navigation Drawer */}
      <AdminNavigationDrawer
        user={user}
        onLogout={handleLogout}
        isMobileOpen={isMobileDrawerOpen}
        onMobileClose={() => setIsMobileDrawerOpen(false)}
        onPinChange={setIsDrawerPinned}
      />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col ${isDrawerPinned ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Header */}
        <header className="bg-neutral-white dark:bg-neutral-black border-b border-neutral-light-gray dark:border-neutral-dark-gray px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setIsMobileDrawerOpen(true)}
                className="md:hidden p-2 hover:bg-gray-100 rounded transition-colors"
                title="Open menu"
              >
                <svg className="w-6 h-6 text-[#080808]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/admin/monitor-chats')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Monitor Chats"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-neutral-black dark:text-neutral-white tracking-tight">
                  Session Details
                </h1>
                {userInfo && (
                  <p className="text-sm text-gray-500 mt-1">
                    {userInfo.isAnonymous && (
                      <span className="inline-flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                    )}
                    {userInfo.userEmail} • {messages.length} messages
                  </p>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-8 w-8" />
                <p className="ml-3 text-gray-600">Loading chat history...</p>
              </div>
            )}

            {/* Session Information */}
            {!isLoading && !error && messages.length > 0 && (
              <div className="bg-white dark:bg-neutral-black border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Session Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {/* User */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">User</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {userInfo?.userEmail || 'Unknown'}
                    </p>
                  </div>

                  {/* Messages */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Messages</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {messages.length}
                    </p>
                  </div>

                  {/* Started */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Started</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {firstMessageTime ? formatDateTime(firstMessageTime) : 'N/A'}
                    </p>
                  </div>

                  {/* Last Activity */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Activity</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {lastActivity ? formatDateTime(lastActivity) : 'N/A'}
                    </p>
                  </div>

                  {/* Estimated Cost */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Estimated Cost</p>
                    <div
                      className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setCostBreakdownExpanded(!costBreakdownExpanded)}
                    >
                      <p className="text-sm font-semibold text-teal-600">
                        ${calculateTotalCost().toFixed(6)}
                      </p>
                      <button className="p-0 text-teal-600 hover:text-teal-700 focus:outline-none">
                        <svg
                          className={`w-4 h-4 transition-transform ${costBreakdownExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    {costBreakdownExpanded && (
                      <div className="mt-3 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Cost Breakdown by Tool
                        </p>
                        <div className="space-y-1">
                          {Object.entries(calculateCostByTool())
                            .sort(([, costA], [, costB]) => costB - costA)
                            .map(([toolName, cost]) => (
                              <div
                                key={toolName}
                                className="flex justify-between items-center py-1 border-b border-teal-200 dark:border-teal-800 last:border-b-0"
                              >
                                <span className="text-xs text-gray-700 dark:text-gray-300">
                                  {toolName}
                                </span>
                                <span className="text-xs font-semibold text-teal-600">
                                  ${cost.toFixed(6)}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Session ID */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Session ID</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-mono text-gray-900 dark:text-gray-100">
                        {sessionId?.substring(0, 8)}...
                      </p>
                      <button
                        onClick={handleCopySessionId}
                        className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                          sessionIdCopied ? 'text-green-600' : 'text-gray-600'
                        }`}
                        title={sessionIdCopied ? 'Copied!' : 'Copy to clipboard'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {sessionIdCopied ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {!isLoading && !error && (
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No messages found in this session</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`rounded-lg shadow-sm ${
                        message.role === 'user'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                          : 'bg-white dark:bg-neutral-black border-l-4 border-teal-500'
                      } ${message.errorOccurred ? 'border-l-4 border-red-500 bg-red-50' : ''}`}
                    >
                      {/* Message Header */}
                      <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              message.role === 'user'
                                ? 'bg-blue-500 text-white'
                                : 'bg-teal-500 text-white'
                            }`}>
                              {message.role === 'user' ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-sm text-gray-900 dark:text-neutral-white">
                                {message.role === 'user' ? 'User Message' : 'Assistant Response'}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {formatDateTime(message.createdAt)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Model Name */}
                            {message.role === 'assistant' && message.modelName && (
                              <span className="text-xs text-gray-600 bg-white dark:bg-gray-700 px-3 py-1 rounded-full border border-gray-300">
                                {message.modelName}
                              </span>
                            )}

                            {/* Error Indicator */}
                            {message.errorOccurred && (
                              <div className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                Error
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Message Content */}
                      <div className="px-4 py-4">
                        {message.role === 'user' ? (
                          <div className="text-gray-900 dark:text-neutral-white text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {message.message}
                          </div>
                        ) : (
                          <div className="prose prose-sm max-w-none dark:prose-invert text-gray-900 dark:text-neutral-white">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                // Paragraphs
                                p: ({ node, ...props }) => (
                                  <p {...props} style={{ margin: '0.5em 0' }} />
                                ),
                                // Unordered and ordered lists
                                ul: ({ node, ...props }) => (
                                  <ul {...props} style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} />
                                ),
                                ol: ({ node, ...props }) => (
                                  <ol {...props} style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} />
                                ),
                                li: ({ node, ...props }) => (
                                  <li {...props} style={{ margin: '0.25em 0' }} />
                                ),
                                // Headings
                                h1: ({ node, ...props }) => (
                                  <h1 {...props} style={{ margin: '0.75em 0 0.5em 0', fontWeight: 'bold' }} />
                                ),
                                h2: ({ node, ...props }) => (
                                  <h2 {...props} style={{ margin: '0.75em 0 0.5em 0', fontWeight: 'bold' }} />
                                ),
                                h3: ({ node, ...props }) => (
                                  <h3 {...props} style={{ margin: '0.75em 0 0.5em 0', fontWeight: 'bold' }} />
                                ),
                                // Blockquotes
                                blockquote: ({ node, ...props }) => (
                                  <blockquote
                                    {...props}
                                    style={{
                                      borderLeft: '3px solid #74AA9C',
                                      paddingLeft: '1em',
                                      margin: '0.5em 0',
                                      fontStyle: 'italic'
                                    }}
                                  />
                                ),
                                // Images
                                img: ({ node, ...props }) => (
                                  <img {...props} className="max-w-full h-auto block" alt={props.alt || ''} />
                                ),
                                // Tables with custom styling
                                table: ({ node, ...props }) => (
                                  <div className="max-w-full max-h-96 overflow-auto my-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-black">
                                    <table
                                      {...props}
                                      className="border-collapse w-full"
                                      style={{
                                        fontSize: '0.85em',
                                        fontFamily: 'Inter, OpenAI Sans, Segoe UI, Helvetica, Arial, sans-serif'
                                      }}
                                    />
                                  </div>
                                ),
                                // Table headers with sticky positioning
                                th: ({ node, ...props }) => (
                                  <th
                                    {...props}
                                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left whitespace-nowrap bg-gray-100 dark:bg-gray-800 font-semibold sticky top-0 z-10"
                                  />
                                ),
                                // Table cells
                                td: ({ node, ...props }) => (
                                  <td
                                    {...props}
                                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left whitespace-nowrap"
                                  />
                                ),
                                // Table rows with alternating colors and hover
                                tr: ({ node, ...props }) => {
                                  const isHeaderRow = props.children && Array.isArray(props.children)
                                    && props.children.some((child: any) => child?.type === 'th');

                                  if (isHeaderRow) {
                                    return <tr {...props} />;
                                  }

                                  return (
                                    <tr
                                      {...props}
                                      className="even:bg-gray-50 dark:even:bg-neutral-black hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                                    />
                                  );
                                },
                                // Code blocks
                                code: ({ node, ...props }) => {
                                  const isInline = !props.className?.includes('language-');
                                  if (isInline) {
                                    return (
                                      <code
                                        {...props}
                                        style={{
                                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                          padding: '0.2em 0.4em',
                                          borderRadius: '3px',
                                          fontFamily: 'monospace',
                                          fontSize: '0.9em'
                                        }}
                                      />
                                    );
                                  }
                                  return (
                                    <code
                                      {...props}
                                      style={{
                                        display: 'block',
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        padding: '0.75em',
                                        borderRadius: '4px',
                                        overflow: 'auto',
                                        margin: '0.5em 0'
                                      }}
                                    />
                                  );
                                },
                                // Links
                                a: ({ node, ...props }) => (
                                  <a
                                    {...props}
                                    style={{
                                      color: '#74AA9C',
                                      textDecoration: 'underline'
                                    }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  />
                                ),
                              }}
                            >
                              {message.message}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>

                      {/* Error Details */}
                      {message.errorOccurred && message.errorDetails && (
                        <div className="mx-4 mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <div className="flex-1">
                              <p className="font-semibold text-red-900 text-sm mb-1">Error Details</p>
                              <p className="text-red-800 text-xs font-mono">{message.errorDetails}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Search Results */}
                      {message.searchResults && message.searchResults.length > 0 && (
                        <div className="mx-4 mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="font-semibold text-blue-900 text-sm mb-2">
                                Search Results ({message.searchResults.length})
                              </p>
                              <div className="space-y-2">
                                {message.searchResults.slice(0, 3).map((result: any, idx: number) => (
                                  <div key={idx} className="text-xs bg-white p-2 rounded border border-blue-200">
                                    <p className="text-blue-900 font-medium">
                                      {result.title || `Result ${idx + 1}`}
                                    </p>
                                    {result.content && (
                                      <p className="text-gray-600 mt-1 line-clamp-2">
                                        {result.content.substring(0, 150)}...
                                      </p>
                                    )}
                                  </div>
                                ))}
                                {message.searchResults.length > 3 && (
                                  <p className="text-blue-700 text-xs italic pl-2">
                                    + {message.searchResults.length - 3} more results
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Tool Usage Steps */}
                      {message.role === 'assistant' && message.steps && message.steps.length > 0 && (
                        <div className="mx-4 mb-6 mt-4">
                          <details
                            className="group bg-amber-50 border-l-4 border-teal-500 rounded overflow-hidden"
                            open={expandedSteps.has(String(message.id))}
                            onToggle={(e) => {
                              // Only toggle if the event is from THIS details element, not a nested one
                              if (e.target === e.currentTarget) {
                                toggleStepsExpanded(String(message.id));
                              }
                            }}
                          >
                            <summary className="cursor-pointer px-4 py-3 flex items-center gap-2 hover:bg-amber-100 transition-colors">
                              <svg
                                className="w-4 h-4 text-teal-600 transition-transform group-open:rotate-90"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="text-sm font-semibold text-gray-800">
                                Tool Usage ({message.steps.length} step{message.steps.length !== 1 ? 's' : ''})
                              </span>
                              {(() => {
                                const totalCost = message.steps.reduce((sum, step) => {
                                  return sum + (step.estimatedCostUsd || 0);
                                }, 0);
                                return totalCost > 0 ? (
                                  <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full ml-auto">
                                    ${totalCost.toFixed(4)}
                                  </span>
                                ) : null;
                              })()}
                            </summary>
                            <div className="px-4 pb-4 space-y-3">
                              {message.steps.map((step, stepIndex) => {
                                const queryKey = `${step.id}-query`;
                                const resultKey = `${step.id}-result`;
                                const llmActivityKey = `${step.id}-llm-activity`;

                                return (
                                  <div key={step.id} className="bg-white border border-gray-200 rounded-lg p-3">
                                    {/* Step Header */}
                                    <div className="flex items-center gap-2 mb-3">
                                      <span className="inline-flex items-center justify-center w-6 h-6 bg-teal-600 text-white text-xs font-bold rounded-full">
                                        {stepIndex + 1}
                                      </span>
                                      <span className="text-sm font-semibold text-teal-700">
                                        {step.llmActivity ? `${step.llmActivity.modelName} (${step.llmActivity.provider})` : step.toolUse}
                                      </span>
                                      {step.estimatedCostUsd && (
                                        <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                                          ${step.estimatedCostUsd.toFixed(4)}
                                        </span>
                                      )}
                                      <span className="ml-auto text-xs text-gray-500">
                                        {formatDateTime(step.timestamp)}
                                      </span>
                                    </div>

                                    {/* Query Section */}
                                    {step.toolQuery && (
                                      <details
                                        className="mb-2 bg-gray-50 border border-gray-200 rounded overflow-hidden"
                                        open={expandedToolSections.has(queryKey)}
                                        onToggle={(e) => {
                                          e.stopPropagation();
                                          toggleToolSection(queryKey);
                                        }}
                                      >
                                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2">
                                          <svg
                                            className="w-3 h-3 transition-transform"
                                            style={{ transform: expandedToolSections.has(queryKey) ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                          Query
                                        </summary>
                                        <div className="p-3 bg-white max-h-72 overflow-auto">
                                          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
                                            {JSON.stringify(step.toolQuery, null, 2)}
                                          </pre>
                                        </div>
                                      </details>
                                    )}

                                    {/* Result Section */}
                                    {step.toolResult && (
                                      <details
                                        className="mb-2 bg-gray-50 border border-gray-200 rounded overflow-hidden"
                                        open={expandedToolSections.has(resultKey)}
                                        onToggle={(e) => {
                                          e.stopPropagation();
                                          toggleToolSection(resultKey);
                                        }}
                                      >
                                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2">
                                          <svg
                                            className="w-3 h-3 transition-transform"
                                            style={{ transform: expandedToolSections.has(resultKey) ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                          Result
                                        </summary>
                                        <div className="p-3 bg-white max-h-72 overflow-auto">
                                          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
                                            {JSON.stringify(step.toolResult, null, 2)}
                                          </pre>
                                        </div>
                                      </details>
                                    )}

                                    {/* LLM Activity Section */}
                                    {step.llmActivity && (
                                      <details
                                        className="bg-purple-50 border border-purple-200 rounded overflow-hidden"
                                        open={expandedToolSections.has(llmActivityKey)}
                                        onToggle={(e) => {
                                          e.stopPropagation();
                                          toggleToolSection(llmActivityKey);
                                        }}
                                      >
                                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-100 transition-colors flex items-center gap-2">
                                          <svg
                                            className="w-3 h-3 transition-transform"
                                            style={{ transform: expandedToolSections.has(llmActivityKey) ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                          </svg>
                                          LLM Activity
                                          <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                                            step.llmActivity.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            step.llmActivity.status === 'failed' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {step.llmActivity.status}
                                          </span>
                                        </summary>
                                        <div className="p-3 bg-white space-y-2">
                                          {/* Metadata Grid */}
                                          <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                              <span className="text-gray-500">Model:</span>
                                              <span className="ml-1 font-medium">{step.llmActivity.modelName} ({step.llmActivity.provider})</span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">Duration:</span>
                                              <span className="ml-1 font-medium">{step.llmActivity.durationMs ? `${step.llmActivity.durationMs}ms` : 'N/A'}</span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">Tokens:</span>
                                              <span className="ml-1 font-medium">
                                                {step.llmActivity.promptTokens || 0} + {step.llmActivity.completionTokens || 0} = {step.llmActivity.totalTokens || 0}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">Cost:</span>
                                              <span className="ml-1 font-medium text-green-700">
                                                ${step.llmActivity.estimatedCostUsd?.toFixed(4) || '0.0000'}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Error Message */}
                                          {step.llmActivity.errorMessage && (
                                            <div className="p-2 bg-red-50 border border-red-200 rounded">
                                              <p className="text-xs text-red-800 font-medium">Error:</p>
                                              <p className="text-xs text-red-700">{step.llmActivity.errorMessage}</p>
                                            </div>
                                          )}

                                          {/* System Prompt */}
                                          {step.llmActivity.systemPrompt && (
                                            <div>
                                              <p className="text-xs font-semibold text-gray-700 mb-1">System Prompt:</p>
                                              <div className="p-2 bg-purple-50 border border-purple-200 rounded max-h-36 overflow-auto">
                                                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                                  {step.llmActivity.systemPrompt}
                                                </pre>
                                              </div>
                                            </div>
                                          )}

                                          {/* Prompt */}
                                          <div>
                                            <p className="text-xs font-semibold text-gray-700 mb-1">Prompt:</p>
                                            <div className="p-2 bg-purple-50 border border-purple-200 rounded max-h-36 overflow-auto">
                                              <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                                {step.llmActivity.prompt}
                                              </pre>
                                            </div>
                                          </div>

                                          {/* Response */}
                                          {step.llmActivity.responseBody && (
                                            <div>
                                              <p className="text-xs font-semibold text-gray-700 mb-1">Response:</p>
                                              <div className="p-2 bg-purple-50 border border-purple-200 rounded max-h-36 overflow-auto">
                                                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                                  {step.llmActivity.responseBody}
                                                </pre>
                                              </div>
                                            </div>
                                          )}

                                          {/* Tool Request */}
                                          {step.llmActivity.toolUseRequest && (
                                            <div>
                                              <p className="text-xs font-semibold text-amber-700 mb-1">Tool Request:</p>
                                              <div className="p-2 bg-amber-50 border border-amber-200 rounded max-h-72 overflow-auto">
                                                <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
                                                  {JSON.stringify(JSON.parse(step.llmActivity.toolUseRequest), null, 2)}
                                                </pre>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
